import { useRef } from 'react';
import { createPortal } from 'react-dom';

import styled from '@emotion/styled';
import { SpriteSvgIcon } from '@kc-console/shared-ui';

import { ServiceButton } from '../ServiceButton';
import { MoreItemServiceButton } from './MoreItemServiceButton';
import { useDragPreviewFollower } from './useDragPreviewFollower';

/**
 * 즐겨찾기 DnD 커스텀 드래그 레이어
 * @description
 * - 각 드래그 소스가 preview(getEmptyImage())로 네이티브 드래그 이미지를 숨기고, 이 레이어가 프리뷰를 직접 렌더한다.
 *   (Safari는 transform 조상 아래 항목의 네이티브 드래그 이미지를 잘못 스냅샷하고 setDragImage에 DOM을 넘겨도 무시함)
 * - 프리뷰는 드래그 중인 아이템 정보로 출처(sourceArea)에 맞는 모양을 렌더한다:
 *   탑바 출처는 탑바 칩(ServiceButton), 메뉴 출처는 메뉴 항목 모양(MoreItemServiceButton, min-width 200px 좌측 정렬).
 * - 위치 추적은 useDragPreviewFollower가 수행한다 — monitor 직접 구독 + ref 직접 write(리렌더 없음),
 *   WebKit dragover throttle로 인한 끊김은 rAF lerp 보간으로 완화.
 * - MUI Popper(더보기 메뉴)가 body로 portal되므로, 레이어도 body로 portal해 navigationDragLayer 계층을 적용한다.
 *   더보기 메뉴는 navigationTop(1310)을 사용하므로 드래그 프리뷰는 그 위인 navigationDragLayer(1320)에 둔다.
 */
const FavoriteDragLayer = () => {
  const previewRef = useRef<HTMLDivElement>(null);
  const item = useDragPreviewFollower(previewRef);

  if (!item) return null;

  return createPortal(
    <PreviewLayer ref={previewRef}>
      {item.sourceArea === 'menu' ? (
        // 메뉴 항목과 동일한 모양/아이콘(흰색 모노) — 서비스명이 짧아도 메뉴 폭(min-width 200px)을 유지
        <MoreItemServiceButton
          startIcon={
            <SpriteSvgIcon fontSize={16} id={item.keyword} category={'BeyondServiceRegular'} />
          }
        >
          {item.name}
        </MoreItemServiceButton>
      ) : (
        <ServiceButton
          startIcon={
            <SpriteSvgIcon
              fontSize={16}
              id={item.keyword}
              category={'BeyondServiceColoredRegular'}
            />
          }
        >
          {item.name}
        </ServiceButton>
      )}
    </PreviewLayer>,
    document.body,
  );
};

export default FavoriteDragLayer;

const PreviewLayer = styled('div')`
  position: fixed;
  top: 0;
  left: 0;
  z-index: ${({ theme }) => theme.zIndex.navigationDragLayer};
  pointer-events: none;
  visibility: hidden;
  will-change: transform;
  /* ServiceButton(variant="inverse", 흰 텍스트/아이콘)이 단독으로 떠 배경이 없으므로 배경을 입힌다 */
  background-color: ${({ theme }) => theme.color['surface/global/transparent/weakest/press']};
  /* 디자인 토큰 boxShadow/elevation/level 2 = theme.shadows[2] (DropdownMenu2 Paper와 동일) */
  box-shadow: ${({ theme }) => theme.shadows[2]};
`;
